import { Select } from '@customerio/pluma-components/react';
import { useDeliveryFilters } from '@/services/db/delivery/queries';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { isEmptyString } from '@/utils/string';
import { getDeliveryChannel } from '../catalog';
import { buildActionOptions, buildCampaignOptions, showActionFilter } from '../utils/message-options';

type CampaignActionFiltersProps = {
  channel: string;
  drafted: boolean;
  campaign: string | null;
  action: string | null;
  onCampaignChange: (value: string | null) => void;
  onActionChange: (value: string | null) => void;
  fixed: ReadonlySet<string>;
  isDisabled?: boolean;
};

/**
 * Label for the campaign select — the (taxonomy-renamed) campaign noun, widened
 * to include broadcasts/transactional for email. Broadcast/Transactional Message
 * stay literal, matching the Ember source.
 */
function campaignSelectLabel(campaignTerm: string, channel: string, drafted: boolean): string {
  let label = campaignTerm;
  if (channel === 'email' && !drafted) label += ', Broadcast,';
  if (channel === 'email') label += ' or Transactional Message';
  return label;
}

/**
 * The campaign + action (message) selects, driven by the workspace's delivery
 * filter options (`useDeliveryFilters`). Ported from the Ember
 * `<cio-deliveries/filters>` "filter" search mode. Rendered under a Suspense
 * boundary by the parent so the rest of the bar isn't blocked on the fetch.
 */
export function CampaignActionFilters({
  channel,
  drafted,
  campaign,
  action,
  onCampaignChange,
  onActionChange,
  fixed,
  isDisabled = false,
}: CampaignActionFiltersProps) {
  const { data: filters } = useDeliveryFilters();
  const taxonomy = useProductTaxonomy();

  const campaignOptions = buildCampaignOptions(filters, { channel, drafted });
  const actionOptions = buildActionOptions(filters, { channel, drafted, campaignValue: campaign });
  const channelName = getDeliveryChannel(channel)?.name ?? 'Action';
  const channelShortName = getDeliveryChannel(channel)?.shortName ?? 'action';

  return (
    <>
      {!fixed.has('campaign') ? (
        <Select
          label={campaignSelectLabel(taxonomy.term('campaigns.titleSingular'), channel, drafted)}
          placeholder="Search by name…"
          value={isEmptyString(campaign) ? undefined : campaign}
          options={campaignOptions}
          onChange={(value) => onCampaignChange(value ?? null)}
          isSearchable
          isClearable
          isDisabled={isDisabled}
          size="sm"
        />
      ) : null}

      {!fixed.has('action') && showActionFilter(campaign) ? (
        <Select
          label={channelName}
          placeholder={`Search by ${channelShortName} name…`}
          value={isEmptyString(action) ? undefined : action}
          options={actionOptions}
          onChange={(value) => onActionChange(value ?? null)}
          isSearchable
          isClearable
          isDisabled={isDisabled}
          size="sm"
        />
      ) : null}
    </>
  );
}
